import React from "react";
import {
  Box,
  Container,
  Grid,
  Link,
  Typography,
  Stack,
  Divider,
  TextField,
  Button,
  IconButton,
  useTheme,
} from "@mui/material";
import {
  Phone,
  Email,
  Send,
  Instagram,
  Telegram,
  Storefront,
  WhatsApp,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import ScrollToTop from "../../components/Product/Utils/ScrollToTop";

// Standard RFC 5322 regex for strict email validation
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export default function FooterUI({
  quickAccessLinks = [],
  contactInfo = { phone: "", phoneRaw: "", email: "" },
  currentYear = new Date().getFullYear(),
  email = "",
  handleEmailChange,
  handleNewsletterSubmit,
  isNewsletterEnabled = true,
  imageSrc,
  handleImageError,
  enamadLink,
}) {
  const theme = useTheme();

  const fallbackImage = "/enamad.png";

  // Helper validation check
  const isValidEmail = (emailStr) => {
    return EMAIL_REGEX.test(emailStr.trim());
  };

  // Button should be disabled if newsletter is turned off OR email is invalid
  const isSubmitDisabled = !isNewsletterEnabled || !isValidEmail(email);

  // WhatsApp support handler
  const handleWhatsAppSupport = () => {
    const waShortLink = "https://wa.me/message/LNAEOJV5FWMQI1";
    window.open(waShortLink, "_blank", "noopener,noreferrer");
  };

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: "grey.50",
        color: "text.primary",
        pt: { xs: 6, md: 8 },
        pb: { xs: 12, sm: 4 },
        direction: "rtl",
        borderTop: "1px solid",
        borderColor: "grey.200",
        position: "relative",
      }}
    >
      <Container maxWidth="lg">
        {/* Top Section: Newsletter & Brand Brief */}
        <Grid container spacing={4} alignItems="center" sx={{ mb: 6 }}>
          {/* Brand Brief (Expands to full width if Newsletter is disabled) */}
          <Grid size={{ xs: 12, md: isNewsletterEnabled ? 6 : 12 }}>
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Storefront color="primary" fontSize="large" />
                <Typography variant="h6" fontWeight={800}>
                  فروشگاه آنلاین زومیو
                </Typography>
              </Stack>
              <Typography
                variant="body2"
                color="text.secondary"
                lineHeight={1.8}
              >
                زومیو، پلتفرمی برای تأمین و خرید عمده کالا ویژه فروشگاه‌ها،
                خرده‌فروشان و کسب‌وکارها. هدف ما فراهم‌کردن تجربه‌ای آسان و
                قابل‌اعتماد برای تهیه کالا و توسعه فعالیت‌های تجاری شماست.
              </Typography>
            </Stack>
          </Grid>

          {/* Newsletter Form (Rendered only if isNewsletterEnabled is true) */}
          {isNewsletterEnabled && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle1" fontWeight={800} mb={1}>
                عضویت در خبرنامه
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                از جدیدترین تخفیف‌ها و پیشنهادات ویژه فروشگاه مطلع شوید.
              </Typography>
              <Box
                component="form"
                onSubmit={handleNewsletterSubmit}
                sx={{ display: "flex", gap: 1, maxWidth: 450 }}
              >
                <TextField
                  fullWidth
                  size="small"
                  type="email"
                  required
                  placeholder="آدرس ایمیل خود را وارد کنید"
                  value={email}
                  onChange={handleEmailChange}
                  slotProps={{
                    htmlInput: {
                      style: { direction: "ltr", textAlign: "right" },
                    },
                  }}
                  sx={{ bgcolor: "background.paper", borderRadius: 1 }}
                />
                <Button
                  disabled={!isValidEmail(email)}
                  type="submit"
                  variant="contained"
                  disableElevation
                  endIcon={<Send sx={{ transform: "rotate(180deg)", mr: 1 }} />}
                  sx={{ whiteSpace: "nowrap", px: 3 }}
                >
                  عضویت
                </Button>
              </Box>
            </Grid>
          )}
        </Grid>

        <Divider sx={{ mb: 6, borderColor: "grey.200" }} />

        {/* Middle Section: Links, Contact, and Badges */}
        <Grid container spacing={4}>
          {/* Column 1: Contact Info (phone blurred until clicked) */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography
              variant="subtitle1"
              fontWeight={800}
              mb={2}
              color="text.primary"
            >
              اطلاعات تماس
            </Typography>
            <Stack spacing={2}>
              {/* Phone Number (gradient-blurred until clicked) */}

              <Stack direction="row" spacing={1.5} alignItems="center">
                <Phone color="action" fontSize="small" />
                <Link
                  href={`tel:${contactInfo.phoneRaw}`}
                  underline="none"
                  color="text.secondary"
                  sx={{
                    fontSize: "0.875rem",
                    transition: "0.2s",
                    direction: "ltr",
                    unicodeBidi: "plaintext",
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  {contactInfo.phone}
                </Link>
              </Stack>

              <Stack direction="row" spacing={1.5} alignItems="center">
                <Email color="action" fontSize="small" />
                <Link
                  href={`mailto:${contactInfo.email}`}
                  underline="none"
                  color="text.secondary"
                  sx={{
                    fontSize: "0.875rem",
                    transition: "0.2s",
                    "&:hover": { color: "primary.main" },
                    direction: "ltr",
                  }}
                >
                  {contactInfo.email}
                </Link>
              </Stack>
            </Stack>
          </Grid>

          {/* Column 2: Quick Access */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography
              variant="subtitle1"
              fontWeight={800}
              mb={2}
              color="text.primary"
            >
              دسترسی سریع
            </Typography>
            <Stack spacing={1.2}>
              {quickAccessLinks.map((link) =>
                link.onClick ? (
                  <Link
                    key={link.text}
                    component="button"
                    type="button"
                    onClick={link.onClick}
                    underline="none"
                    color="text.secondary"
                    sx={{
                      fontSize: "0.875rem",
                      transition: "0.2s",
                      textAlign: "right",
                      cursor: "pointer",
                      background: "none",
                      border: "none",
                      p: 0,
                      "&:hover": {
                        color: "primary.main",
                        transform: "translateX(-4px)",
                      },
                    }}
                  >
                    {link.text}
                  </Link>
                ) : (
                  <Link
                    component={RouterLink}
                    to={link.href}
                    key={link.text}
                    underline="none"
                    color="text.secondary"
                    sx={{
                      fontSize: "0.875rem",
                      transition: "0.2s",
                      "&:hover": {
                        color: "primary.main",
                        transform: "translateX(-4px)",
                      },
                    }}
                  >
                    {link.text}
                  </Link>
                ),
              )}
            </Stack>
          </Grid>

          {/* Column 4: Trust Badges */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Stack spacing={2} alignItems={{ xs: "center", sm: "flex-start" }}>
              <Typography
                variant="subtitle1"
                fontWeight={800}
                color="text.primary"
              >
                مجوزها
              </Typography>
              <Stack direction="row" spacing={1.5}>
                <Box
                  sx={{
                    width: 80,
                    height: 90,
                    bgcolor: "background.paper",
                    border: "1px solid",
                    borderColor: "grey.200",
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    transition: "0.2s",
                    p: 1,
                    "&:hover": { borderColor: "primary.main", boxShadow: 1 },
                  }}
                >
                  <a
                    referrerPolicy="origin"
                    target="_blank"
                    href={enamadLink}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    <img
                      referrerPolicy="origin"
                      src={imageSrc}
                      alt="نماد اعتماد الکترونیکی (اینماد)"
                      onError={handleImageError}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        cursor: "pointer",
                      }}
                    />
                  </a>
                </Box>
              </Stack>
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, borderColor: "grey.200" }} />

        {/* Bottom Section: Copyright, Social Media & Scroll To Top */}
        <Stack spacing={2.5}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              mx: { md: -2 },
              px: { md: 2 },
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              {/* Whatsapp  */}
              <IconButton
                onClick={handleWhatsAppSupport}
                size="small"
                sx={{
                  color: "#25D366", // WhatsApp Green
                  bgcolor: "rgba(37, 211, 102, 0.1)",
                  width: 36,
                  height: 36,
                  transition: "0.2s",
                  "&:hover": {
                    bgcolor: "#25D366",
                    color: "#fff",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <WhatsApp fontSize="small" />
              </IconButton>

              <IconButton
                disabled
                size="small"
                color="inherit"
                sx={{
                  color: "text.secondary",
                  bgcolor: "grey.100",
                  width: 36,
                  height: 36,
                  "&.Mui-disabled": {
                    bgcolor: "grey.100",
                    opacity: 0.5,
                    pointerEvents: "none",
                  },
                }}
              >
                <Instagram fontSize="small" />
              </IconButton>
              <IconButton
                disabled
                size="small"
                color="inherit"
                sx={{
                  color: "text.secondary",
                  bgcolor: "grey.100",
                  width: 36,
                  height: 36,
                  "&.Mui-disabled": {
                    bgcolor: "grey.100",
                    opacity: 0.5,
                    pointerEvents: "none",
                  },
                }}
              >
                <Telegram fontSize="small" />
              </IconButton>
            </Stack>

            <ScrollToTop />
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            align="center"
            sx={{ display: "block" }}
          >
            تمامی حقوق مادی و معنوی این وب‌سایت متعلق به{" "}
            <Box component="span" fontWeight={700} color="text.primary">
              فروشگاه آنلاین زومیو
            </Box>{" "}
            می‌باشد.
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
}
